import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import FilterLoginScreen from "./FilterLoginScreen";
import NewStockDataPage from "./NewStockDataPage";
import GuestProductList from "./GuestProductList";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const ProtectedStockDataRoute = () => {
  const { linkName, store_id, linkId, mfr_id, encryptedType, contactDM } =
    useParams();

  const [linkType, setLinkType] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDecryptionError, setIsDecryptionError] = useState(false);
  const [loginUserInfo, setLoginUserInfo] = useState(null);
  const [loginUserSessionInfo, setLoginUserSessionInfo] = useState(null);
  const [askLogin, setAskLogin] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // ✅ UNIFIED STORAGE KEY - Same for all links
  const UNIFIED_STORAGE_KEY = "FILTER_LINK_LOGIN_UNIFIED";

  const effectiveStoreId = store_id || storeInfo?.id;
  const effectiveContactDM =
    contactDM || storeInfo?.contact_dm || storeInfo?.mobile;

  // Decrypt link type
  const decodeType = (encoded) => {
    const salt = "XyZ123";
    let base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4 !== 0) {
      base64 += "=";
    }
    const decoded = atob(base64);
    const [type] = decoded.split("::");
    return type;
  };

  useEffect(() => {
    try {
      const type = decodeType(encryptedType);
      if (type === "public" || type === "private") {
        setLinkType(type);
      } else {
        setIsDecryptionError(true);
      }
    } catch (err) {
      console.error("Error decoding type:", err);
      setIsDecryptionError(true);
    }
  }, [encryptedType]);

  // Fetch store information
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/auth/storeInfo/${mfr_id}`,
        );

        if (response.data?.statusCode === 200) {
          setStoreInfo(response.data?.data);
        }
      } catch (error) {
        console.error("Error fetching store info:", error);
        setStoreInfo(null);
      }
    };

    if (mfr_id) {
      fetchStoreInfo();
    }
  }, [mfr_id]);

  // ✅ FIXED: Only auto-login for PUBLIC links
  useEffect(() => {
    const checkAutoLogin = async () => {
      if (!linkType || !linkId) {
        setIsCheckingAccess(false);
        return;
      }

      // 🔒 PRIVATE LINKS: Don't check access here, always show guest page first
      if (linkType === "private") {
        setIsCheckingAccess(false);
        return;
      }

      // 🟢 PUBLIC LINK: Check for auto-login
      const saved = localStorage.getItem(UNIFIED_STORAGE_KEY);
      if (!saved) {
        setIsCheckingAccess(false);
        return;
      }

      try {
        const parsed = JSON.parse(saved);
        if (!parsed?.user) {
          localStorage.removeItem(UNIFIED_STORAGE_KEY);
          setIsCheckingAccess(false);
          return;
        }

        setIsLoggedIn(true);
        setLoginUserInfo(parsed.user);
      } catch (err) {
        console.error("Failed to parse saved login", err);
        localStorage.removeItem(UNIFIED_STORAGE_KEY);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAutoLogin();
  }, [linkType, linkId]);

  const handleAskLogin = () => {
    setAskLogin(true);
  };

  const handleLoginSuccess = (userInfo, sessionInfo = null) => {
    setIsLoggedIn(true);
    setLoginUserInfo(userInfo);
    if (sessionInfo) {
      setLoginUserSessionInfo(sessionInfo);
    }
  };

  // Invalid link handler
  if (isDecryptionError) {
    return (
      <div
        style={{
          color: "#fff",
          backgroundColor: "#394A5C",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          fontWeight: 600,
        }}
      >
        Invalid or expired link 🔒
      </div>
    );
  }

  if (!linkType) return null;

  // Wait for store info
  if (!storeInfo) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
          fontWeight: 600,
        }}
      >
        Loading…
      </div>
    );
  }

  // Show loading while checking access (only for public links)
  if (isCheckingAccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontWeight: 600, color: "#666" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // ==========================================
  // PUBLIC LINK FLOW
  // ==========================================
  if (linkType === "public") {
    return !isLoggedIn ? (
      <FilterLoginScreen
        storeInformation={storeInfo}
        onLoginSuccess={handleLoginSuccess}
        linkId={linkId}
        linkName={linkName}
        store_id={effectiveStoreId}
        mfr_id={mfr_id}
        linkPrivacy={linkType}
        contactDM={effectiveContactDM}
      />
    ) : (
      <NewStockDataPage
        linkType={linkType}
        linkName={linkName}
        linkId={linkId}
        mfr_id={mfr_id}
        userInfo={loginUserInfo}
        sessionInfo={null}
        setSessionInfo={() => {}}
      />
    );
  }

  // ==========================================
  // PRIVATE LINK FLOW
  // ==========================================
  if (linkType === "private") {
    // Show guest view first (always for private links)
    if (!askLogin) {
      return (
        <GuestProductList
          linkType={linkType}
          linkName={linkName}
          linkId={linkId}
          mfr_id={mfr_id}
          handleAskLogin={handleAskLogin}
        />
      );
    }

    // User clicked login - now show login screen or auto-login
    return !isLoggedIn ? (
      <FilterLoginScreen
        storeInformation={storeInfo}
        onLoginSuccess={handleLoginSuccess}
        linkId={linkId}
        linkName={linkName}
        store_id={effectiveStoreId}
        mfr_id={mfr_id}
        linkPrivacy={linkType}
        contactDM={effectiveContactDM}
      />
    ) : (
      <NewStockDataPage
        linkType={linkType}
        linkName={linkName}
        linkId={linkId}
        mfr_id={mfr_id}
        userInfo={loginUserInfo}
        sessionInfo={loginUserSessionInfo}
        setSessionInfo={setLoginUserSessionInfo}
      />
    );
  }

  // Fallback
  return (
    <div
      style={{
        color: "#fff",
        backgroundColor: "#394A5C",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        fontWeight: 600,
      }}
    >
      Invalid link
    </div>
  );
};

export default ProtectedStockDataRoute;
